import type { ReactNode } from 'react';

export function Card({
  children,
  className = '',
  id,
}: {
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <div id={id} className={`rounded-xl border border-[#26304d] bg-[#141a2e]/70 shadow-lg shadow-black/20 ${className}`}>
      {children}
    </div>
  );
}

const TONES = {
  info: 'border-[#4f8cff]/40 bg-[#4f8cff]/10 text-[#bcd4ff]',
  good: 'border-[#22d3a6]/40 bg-[#22d3a6]/10 text-[#a7f3dc]',
  warn: 'border-[#f5a623]/40 bg-[#f5a623]/10 text-[#ffe0ad]',
  bad: 'border-[#ff5c72]/40 bg-[#ff5c72]/10 text-[#ffc2ca]',
} as const;

export type Tone = keyof typeof TONES;

export function Callout({ tone = 'info', title, children }: { tone?: Tone; title?: string; children: ReactNode }) {
  return (
    <div className={`rounded-lg border px-4 py-3 text-[13px] leading-relaxed ${TONES[tone]}`}>
      {title && <div className="mb-1 font-semibold">{title}</div>}
      <div className="opacity-90">{children}</div>
    </div>
  );
}

export function Badge({ tone = 'info', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`inline-block rounded border px-1.5 py-0.5 text-[11px] font-medium ${TONES[tone]}`}>{children}</span>;
}

export function Button({
  children,
  onClick,
  variant = 'primary',
  disabled,
  type = 'button',
  className = '',
  title,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'ghost' | 'danger' | 'soft';
  disabled?: boolean;
  type?: 'button' | 'submit';
  className?: string;
  title?: string;
}) {
  const styles = {
    primary: 'bg-[#4f8cff] text-white hover:bg-[#3f7ae8]',
    soft: 'bg-[#22d3a6]/15 text-[#7ef0d0] border border-[#22d3a6]/30 hover:bg-[#22d3a6]/25',
    ghost: 'bg-white/5 text-slate-200 border border-[#26304d] hover:bg-white/10',
    danger: 'bg-[#ff5c72]/15 text-[#ffb0bb] border border-[#ff5c72]/30 hover:bg-[#ff5c72]/25',
  } as const;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-40 ${styles[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-slate-300">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-slate-500">{hint}</span>}
    </label>
  );
}

export const inputCls =
  'w-full rounded-lg border border-[#26304d] bg-[#0b1020] px-3 py-2 text-sm text-slate-100 outline-none focus:border-[#4f8cff] placeholder:text-slate-600';

export function Money({ v }: { v: number }) {
  return <span>{v.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}</span>;
}

export function Stars({ value, count }: { value: number; count: number }) {
  return (
    <span className="inline-flex items-center gap-1 text-[11.5px] text-slate-500">
      <span className="text-[#f5a623]">{'★'.repeat(Math.round(value))}</span>
      <span>
        {value.toFixed(1)} ({count})
      </span>
    </span>
  );
}
